import React from 'react'
import toastr from 'toastr'
import { useMutation } from '../../../../api/graphql'
import ResponsiveImage from '../../misc/MediaPresets'
import MemberCard from './MemberCard'
import {
  EmailIcon,
  GlobeIcon,
  LocationIcon,
  LogOutIcon,
  PhoneIcon,
  UsersIcon,
} from './OrganizationIcons'
import {
  ORGANIZATION_TYPE_LABELS,
  StyledAddressRow,
  StyledDetailContainer,
  StyledDivider,
  StyledFieldGroup,
  StyledFieldsSection,
  StyledLabel,
  StyledLabelWithIcon,
  StyledLeaveButton,
  StyledLeaveSection,
  StyledMemberGrid,
  StyledMembersHeader,
  StyledMembersSection,
  StyledMembersTitle,
  StyledReadOnlyField,
  StyledReadOnlyFieldSmall,
  StyledReadOnlyLogoPreview,
  StyledTwoColumnRow,
} from './OrganizationDetail.styles'
import type { Organization } from './OrganizationsSection'
import { OrganizationMembershipsQuery } from './OrganizationsSection'
import gql from 'graphql-tag'

const LEAVE_ORGANIZATION_MUTATION = gql`
  mutation OrganizationMembershipLeave($input: OrganizationMembershipLeaveInput!) {
    organizationMembershipLeave(input: $input) {
      success
      errors {
        attribute
        message
      }
    }
  }
`

type OrganizationDetailReadOnlyProps = {
  organization: Organization
  currentUserId?: string
  membershipId?: string
}

const OrganizationDetailReadOnly: React.FC<OrganizationDetailReadOnlyProps> = ({
  organization,
  currentUserId,
  membershipId,
}) => {
  const [leaveOrganization, { loading: leaveLoading }] = useMutation(LEAVE_ORGANIZATION_MUTATION)

  const handleLeaveOrganization = async () => {
    if (!membershipId) return

    if (!window.confirm(`Are you sure you want to leave ${organization.name}?`)) {
      return
    }

    try {
      const result = await leaveOrganization({
        variables: {
          input: { id: membershipId },
        },
        refetchQueries: [{ query: OrganizationMembershipsQuery }],
      })

      const response = result.data?.organizationMembershipLeave
      if (response?.errors?.length > 0) {
        toastr.error(response.errors[0].message)
        return
      }

      if (response?.success) {
        toastr.success(`You have left ${organization.name}`)
      }
    } catch (error) {
      toastr.error('Failed to leave organization')
    }
  }

  return (
    <StyledDetailContainer>
      <StyledFieldsSection>
        <StyledFieldGroup>
          <StyledLabel>Organization Name</StyledLabel>
          <StyledReadOnlyField>{organization.name}</StyledReadOnlyField>
        </StyledFieldGroup>

        <StyledFieldGroup>
          <StyledLabel>Organization Type</StyledLabel>
          <StyledReadOnlyField>
            {organization.organizationType
              ? ORGANIZATION_TYPE_LABELS[organization.organizationType] || organization.organizationType
              : '-'}
          </StyledReadOnlyField>
        </StyledFieldGroup>

        {organization.logoPublicId && (
          <StyledFieldGroup>
            <StyledLabel>Organization Logo</StyledLabel>
            <StyledReadOnlyLogoPreview>
              <ResponsiveImage publicId={organization.logoPublicId} width="80" height="80" />
            </StyledReadOnlyLogoPreview>
          </StyledFieldGroup>
        )}

        <StyledFieldGroup>
          <StyledLabelWithIcon>
            <LocationIcon />
            Address
          </StyledLabelWithIcon>
          <StyledReadOnlyField>{organization.streetAddress || '-'}</StyledReadOnlyField>
          <StyledAddressRow>
            <StyledReadOnlyFieldSmall>{organization.city || '-'}</StyledReadOnlyFieldSmall>
            <StyledReadOnlyFieldSmall>{organization.state || '-'}</StyledReadOnlyFieldSmall>
            <StyledReadOnlyFieldSmall>{organization.zip || '-'}</StyledReadOnlyFieldSmall>
          </StyledAddressRow>
        </StyledFieldGroup>

        <StyledTwoColumnRow>
          <StyledFieldGroup>
            <StyledLabelWithIcon>
              <GlobeIcon />
              Website
            </StyledLabelWithIcon>
            <StyledReadOnlyField>{organization.websiteUrl || '-'}</StyledReadOnlyField>
          </StyledFieldGroup>

          <StyledFieldGroup>
            <StyledLabelWithIcon>
              <PhoneIcon />
              Phone
            </StyledLabelWithIcon>
            <StyledReadOnlyField>{organization.phone || '-'}</StyledReadOnlyField>
          </StyledFieldGroup>
        </StyledTwoColumnRow>

        <StyledFieldGroup>
          <StyledLabelWithIcon>
            <EmailIcon />
            Contact Email
          </StyledLabelWithIcon>
          <StyledReadOnlyField>{organization.contactEmail || '-'}</StyledReadOnlyField>
        </StyledFieldGroup>
      </StyledFieldsSection>

      <StyledDivider />

      <StyledMembersSection>
        <StyledMembersHeader>
          <StyledMembersTitle>
            <UsersIcon />
            Organization Members
          </StyledMembersTitle>
        </StyledMembersHeader>

        <StyledMemberGrid>
          {organization.organizationMemberships.map(membership => (
            <MemberCard
              key={membership.id}
              membership={membership}
              isCurrentUser={membership.user?.id === currentUserId}
              isOwnerViewing={false}
            />
          ))}
        </StyledMemberGrid>
      </StyledMembersSection>

      <StyledDivider />

      <StyledLeaveSection>
        <StyledLeaveButton type="button" onClick={handleLeaveOrganization} disabled={leaveLoading}>
          <LogOutIcon />
          {leaveLoading ? 'Leaving...' : 'Leave Organization'}
        </StyledLeaveButton>
      </StyledLeaveSection>
    </StyledDetailContainer>
  )
}

export default OrganizationDetailReadOnly
